import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskTextLoad, FadeUp } from "@/components/Reveal";
import { LogoBadge } from "@/components/Logo";
import { INVITATION, LANDING_SLIDES, MISSION } from "@/data/content";

export default function Landing() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % LANDING_SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#1B0812] text-cream overflow-hidden" data-testid="landing-page">
      {/* Background slideshow */}
      <div className="fixed inset-0 overflow-hidden" data-testid="landing-slideshow">
        <AnimatePresence>
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 1, scale: 1.0 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.8, ease: "easeInOut" }, scale: { duration: 7, ease: "linear" } }}
            className="absolute inset-0"
          >
            <img src={LANDING_SLIDES[i]} alt="" className="h-full w-full object-cover object-top" />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-[#1B0812]/72" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B0812] via-[#1B0812]/80 to-[#1B0812]/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B0812] via-transparent to-[#1B0812]/50" />
        <div className="absolute inset-0 grain" />
      </div>

      <div className="absolute -top-24 right-[-8rem] h-[30rem] w-[30rem] rounded-full brand-gradient blur-[150px] opacity-25 animate-floaty pointer-events-none" />

      {/* Top bar */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 py-6 flex items-center justify-between"
      >
        <Link to="/" data-testid="landing-logo"><LogoBadge /></Link>
        <Link
          to="/home"
          data-testid="landing-skip"
          className="font-display text-xs uppercase tracking-[0.2em] text-cream/70 hover:text-coral transition-colors"
        >
          Enter Site →
        </Link>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1100px] px-5 md:px-10 pt-10 md:pt-16 pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-display text-xs uppercase tracking-[0.3em] text-coral mb-8"
        >
          Our Invitation
        </motion.p>

        <h1 className="font-serif font-light text-[13vw] md:text-[7vw] leading-[0.9] tracking-tight text-cream drop-shadow-[0_2px_30px_rgba(0,0,0,0.5)]">
          <MaskTextLoad lines={["The future is waiting", "for this generation."]} delay={0.3} stagger={0.14} />
        </h1>

        <FadeUp delay={0.5}>
          <div className="mt-10 max-w-2xl border-l-2 border-coral pl-6">
            <p className="font-display text-[0.65rem] uppercase tracking-[0.28em] text-coral mb-4">Our Mission</p>
            <p className="font-serif font-light text-xl md:text-2xl leading-relaxed text-cream/90">{MISSION}</p>
          </div>
        </FadeUp>

        <div className="mt-10 space-y-5 max-w-2xl">
          {INVITATION.paras.slice(1).map((p, idx) => (
            <FadeUp key={idx} delay={0.6 + idx * 0.06}>
              <p className="font-sans text-base md:text-lg leading-relaxed text-cream/80">{p}</p>
            </FadeUp>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-3 max-w-3xl">
          {INVITATION.questions.map((q, idx) => (
            <FadeUp key={q} delay={0.7 + idx * 0.05} className="border-l-2 border-coral/70 pl-5">
              <span className="font-serif italic text-xl md:text-2xl text-cream/90">{q}</span>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.9}>
          <p className="mt-12 font-serif text-2xl md:text-4xl leading-snug text-cream max-w-3xl">{INVITATION.begin}</p>
        </FadeUp>
        <FadeUp delay={1.0}>
          <p className="mt-8 font-sans text-base md:text-lg leading-relaxed text-cream/80 max-w-2xl">{INVITATION.welcome}</p>
        </FadeUp>

        <FadeUp delay={1.1}>
          <p className="mt-12 font-display text-3xl md:text-6xl font-black uppercase tracking-tight text-gradient">
            {INVITATION.motto}
          </p>
        </FadeUp>

        <FadeUp delay={1.2}>
          <p className="mt-10 font-serif italic font-light text-xl md:text-2xl leading-relaxed text-cream/90 max-w-3xl">
            {INVITATION.closing}
          </p>
        </FadeUp>

        <FadeUp delay={1.3}>
          <div className="mt-14 flex flex-wrap items-center gap-4">
            <Link
              to="/home"
              data-testid="landing-enter"
              className="group inline-flex items-center gap-3 rounded-full brand-gradient px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_34px_rgba(232,38,111,0.5)]"
            >
              Enter the Movement
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/register"
              data-testid="landing-register"
              className="inline-flex rounded-full border border-white/40 px-9 py-4 font-display text-sm uppercase tracking-[0.14em] font-semibold text-cream transition-all duration-300 hover:bg-white/10"
            >
              Register Interest
            </Link>
          </div>
        </FadeUp>

        {/* slide indicators */}
        <div className="mt-16 flex gap-2" data-testid="landing-dots">
          {LANDING_SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              aria-label={`Slide ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${idx === i ? "w-10 brand-gradient" : "w-4 bg-white/25 hover:bg-white/50"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
